/**
 * Accordion Web Component
 * -----------------------
 * This component defines a custom HTML element <accordion-component>
 * that provides an accessible and interactive accordion UI.
 *
 * Features:
 * - Supports dynamic content using a MutationObserver.
 * - Toggles content panels open and closed with smooth transitions.
 * - Automatically adds expand/collapse icons.
 * - Ensures only one section is open at a time.
 * - Keyboard accessible (Enter/Space to toggle).
 *
 * Usage:
 *  <accordion-component>
 *    <div class="accordion-title">Title 1</div>
 *    <div class="accordion-content">Content for panel 1</div>
 *    <div class="accordion-title">Title 2</div>
 *    <div class="accordion-content">Content for panel 2</div>
 *  </accordion-component>
 */

const Accordion = (function () {
  // --- Create a <template> element that defines styles for the accordion ---
  const template = document.createElement("template");
  template.innerHTML = `
  <style>
    .accordion {
      display: flex;
      flex-direction: column;
      width: 100%;
    }

    .accordion .accordion-title {
      cursor: pointer;
      padding: 2.25rem 0;
      font-weight: 600;
      border-top: 1px solid rgba(65, 61, 60, 0.1);
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 1.125rem;
      background-color: transparent;
      text-align: left;
    }

    .accordion .accordion-title:first-child {
      border-top: none;
    }
    .accordion .accordion-title-inner {
      flex-grow: 1;
    }
    .accordion .accordion-title:focus-visible .accordion-title-inner {
      outline: 2px solid #1570ef;
      outline-offset: 2px;
    }

    .accordion .icon {
      font-size: 2.25rem;
      font-weight: 100;
      width: 3.125rem;
      text-align: center;
    }

    .accordion .accordion-content {
      overflow: hidden;
      height: 0;
      transition: height 0.25s ease;
      position: relative;
      top: -2.25rem;
    }

    .accordion .accordion-content-inner {
      padding-top: .45rem;
      padding-bottom: 1.8rem;
      margin-right: 3.125rem;
      font-size: 1rem;
      font-weight: 400;
      color: #2C2C2C;
    }
  </style>

  `;

  /**
   * Accordion custom element definition.
   */
  class Accordion extends HTMLElement {
    constructor() {
      super();
      this.observer = null; // Watches for DOM changes (new titles/content)
    }

    /**
     * Called when the element is inserted into the DOM.
     * Sets up template, event listeners, and initializes accordion behavior.
     */
    connectedCallback() {
      // Clone and attach template styles
      this.appendChild(template.content.cloneNode(true));

      // Create main accordion container
      this.container = document.createElement("div");
      this.container.classList.add("accordion");
      this.appendChild(this.container);

      // Watch for dynamic changes to child nodes
      this.observer = new MutationObserver(() => this.initializeAccordion());
      this.observer.observe(this, { childList: true });

      // Initial setup
      this.initializeAccordion();
    }

    /**
     * Initializes accordion structure and sets up interactivity.
     * Moves .accordion-title and .accordion-content elements into the main container,
     * adds icons, and attaches event handlers.
     */
    initializeAccordion() {
      // Gather only relevant nodes (.accordion-title or .accordion-content)
      const nodes = Array.from(this.children).filter(
        (el) =>
          el !== this.container &&
          (el.classList.contains("accordion-title") ||
            el.classList.contains("accordion-content")),
      );

      // If no valid nodes, exit
      if (nodes.length === 0) return;

      // Move valid nodes into container
      nodes.forEach((node) => this.container.appendChild(node));

      // Find all title elements
      const titles = this.container.querySelectorAll(".accordion-title");

      // Set up each title with icon and toggle behavior
      titles.forEach((title, index) => this.buildAndBindTitle(title, index));
    }

    // Sets up a title element with icon and toggle behavior
    buildAndBindTitle(title, index) {
      // --- Find corresponding content element ---
      const content = title.nextElementSibling;
      if (!content || !content.classList.contains("accordion-content")) return;

      // --- Associate content with title for screen readers ---
      const contentId = `accordion-content-${index}`;
      content.id = contentId;
      title.setAttribute("aria-controls", contentId);
      title.setAttribute("aria-expanded", "false");
      content.setAttribute("aria-hidden", "true");
      content.inert = true; // Accessibility: make non-interactive when hidden

      // --- Ensure title has inner wrapper for accessibility ---
      if (!title.querySelector(".accordion-title-inner")) {
        const titleWrapper = document.createElement("div");
        titleWrapper.classList.add("accordion-title-inner");
        while (title.firstChild) titleWrapper.appendChild(title.firstChild);
        title.appendChild(titleWrapper);
      }

      // --- Ensure content has inner wrapper for padding ---
      if (!content.querySelector(".accordion-content-inner")) {
        const wrapper = document.createElement("div");
        wrapper.classList.add("accordion-content-inner");
        while (content.firstChild) wrapper.appendChild(content.firstChild);
        content.appendChild(wrapper);
      }

      // --- Add toggle event if not already bound ---
      if (!title._bound) {
        // Click event for mouse users
        title.addEventListener("click", () => this.toggle(content, title));
        // Keyboard accessibility (Enter / Space toggles)
        title.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.toggle(content, title);
          }
        });
        title._bound = true; // Mark title as initialized
      }

      // --- Add toggle icon if missing ---
      let icon = title.querySelector(".icon");
      if (!icon) {
        icon = document.createElement("span");
        icon.classList.add("icon");
        icon.textContent = "+";
        icon.setAttribute("aria-hidden", "true"); // Decorative only
        icon.setAttribute("alt", ""); // Decorative only
        title.appendChild(icon);
      }
    }

    // Toggle accordion content open/closed
    toggle(content, title) {
      const isOpen = content.style.height && content.style.height !== "0px";

      // Close all other content sections
      this.container.querySelectorAll(".accordion-content").forEach((c) => {
        c.style.height = "0px";
        c.setAttribute("aria-hidden", "true"); // Accessibility
        const t = c.previousElementSibling;
        if (t) t.setAttribute("aria-expanded", "false"); // Accessibility
        const i = t.querySelector(".icon");
        if (i) i.textContent = "+";
        c.inert = true; //Accessibility
      });

      // If the current one isn't open, expand it
      if (!isOpen) {
        const scrollHeight = content.scrollHeight;
        content.style.height = scrollHeight + "px";
        content.setAttribute("aria-hidden", "false"); // Accessibility
        title.setAttribute("aria-expanded", "true"); // Accessibility
        const icon = title.querySelector(".icon");
        if (icon) icon.textContent = "–";
        content.inert = false; //Accessibility
      }
    }
  }

  // --- Register custom element ---
  customElements.define("accordion-component", Accordion);

  return Accordion;
})();

export { Accordion };
